/**
 * Guardian dashboard.
 *
 * Shows each child's reading level and recent accuracy, and provides the two
 * Loi 25 (Quebec privacy law) controls:
 *   - Export all — a synchronous download of every record (access request).
 *   - Delete all — a cascade erasure of all children's data (erasure request,
 *     30-day window). Guarded by an explicit type-to-confirm step.
 *
 * Reaching this screen requires the guardian PIN to be unlocked (enforced by
 * the router; this component assumes an unlocked session).
 */

import { useEffect, useState } from "react";

import {
  children as childrenApi,
  guardian,
  books,
  feedback,
  READING_LEVELS,
  type Child,
  type FixMissingImagesProgress,
  type MasteryView,
} from "@/lib/api";

interface ChildRow {
  child: Child;
  mastery: MasteryView | null;
}

const AVATAR_CHOICES = ["🦊", "🐼", "🦄", "🐙", "🦕", "🐝", "🌟", "🚀"] as const;

/**
 * A compact SVG line graph of a child's past quiz scores (0..1), drawn oldest
 * to newest so grown-ups can see progress over time at a glance.
 */
function ScoreHistoryGraph({
  scores,
  label,
}: {
  scores: number[];
  label: string;
}) {
  if (scores.length < 2) {
    return null;
  }
  const width = 120;
  const height = 36;
  const pad = 3;
  const points = scores
    .map((score, i) => {
      const x =
        pad + (i / (scores.length - 1)) * (width - pad * 2);
      const clamped = Math.max(0, Math.min(1, score));
      const y = height - pad - clamped * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      role="img"
      aria-label={`${label} past quiz scores over time`}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      style={{ display: "block", marginTop: "0.35rem" }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--color-grape)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Dashboard() {
  const [rows, setRows] = useState<ChildRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const [newNickname, setNewNickname] = useState("");
  const [newAvatar, setNewAvatar] = useState<string>(AVATAR_CHOICES[0]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [savingLevelId, setSavingLevelId] = useState<string | null>(null);
  const [levelError, setLevelError] = useState<string | null>(null);

  const [cleaning, setCleaning] = useState(false);
  const [cleanupCount, setCleanupCount] = useState<number | null>(null);
  const [cleanupError, setCleanupError] = useState<string | null>(null);

  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState<{
    booksScanned: number;
    pagesFixed: number;
    pagesFailed: number;
  } | null>(null);
  const [fixError, setFixError] = useState<string | null>(null);
  const [fixProgress, setFixProgress] =
    useState<FixMissingImagesProgress | null>(null);

  const [feedbackKind, setFeedbackKind] = useState<"bug" | "feature">("bug");
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackBody, setFeedbackBody] = useState("");
  const [sending, setSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackResult, setFeedbackResult] = useState<{
    status: "created" | "queued_locally";
    issueUrl: string | null;
  } | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const kids = await childrenApi.list();
        const withMastery = await Promise.all(
          kids.map(async (child) => {
            try {
              const mastery = await childrenApi.mastery(child.id);
              return { child, mastery };
            } catch {
              return { child, mastery: null };
            }
          }),
        );
        if (active) setRows(withMastery);
      } catch {
        if (active) setError("Could not load the dashboard.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function handleAddChild(event: React.FormEvent) {
    event.preventDefault();
    const nickname = newNickname.trim();
    if (!nickname || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      const child = await childrenApi.create({ nickname, avatar: newAvatar });
      setRows((prev) => [...prev, { child, mastery: null }]);
      setNewNickname("");
      setNewAvatar(AVATAR_CHOICES[0]);
    } catch {
      setAddError("Could not add that child. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  async function handleChangeLevel(childId: string, level: string) {
    setSavingLevelId(childId);
    setLevelError(null);
    try {
      const updated = await childrenApi.update(childId, { readingLevel: level });
      setRows((prev) =>
        prev.map((row) =>
          row.child.id === childId
            ? {
                child: updated,
                mastery: row.mastery
                  ? { ...row.mastery, level: updated.readingLevel }
                  : row.mastery,
              }
            : row,
        ),
      );
    } catch {
      setLevelError("Could not change that level. Please try again.");
    } finally {
      setSavingLevelId(null);
    }
  }

  async function handleCleanupIncomplete() {
    if (cleaning) return;
    setCleaning(true);
    setCleanupError(null);
    try {
      const result = await books.deleteIncomplete();
      setCleanupCount(result.deleted);
    } catch {
      setCleanupError("Cleanup failed. No books were removed.");
    } finally {
      setCleaning(false);
    }
  }

  async function handleFixMissingImages() {
    if (fixing) return;
    setFixing(true);
    setFixError(null);
    setFixResult(null);
    setFixProgress(null);
    try {
      const result = await books.fixMissingImages((progress) => {
        setFixProgress(progress);
      });
      setFixResult(result);
    } catch {
      setFixError("Could not fix the pictures right now. Please try again.");
    } finally {
      setFixing(false);
      setFixProgress(null);
    }
  }

  async function handleSendFeedback(event: React.FormEvent) {
    event.preventDefault();
    const title = feedbackTitle.trim();
    const body = feedbackBody.trim();
    if (!title || !body || sending) return;
    setSending(true);
    setFeedbackError(null);
    setFeedbackResult(null);
    try {
      const result = await feedback.create({ kind: feedbackKind, title, body });
      setFeedbackResult({ status: result.status, issueUrl: result.issueUrl });
      setFeedbackTitle("");
      setFeedbackBody("");
    } catch {
      setFeedbackError("Could not send your feedback. Please try again.");
    } finally {
      setSending(false);
    }
  }

  async function handleDeleteAll() {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    setError(null);
    try {
      await guardian.deleteAll();
      setDeleted(true);
      setRows([]);
    } catch {
      setError("Delete failed. No data was removed.");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <p role="status">Loading dashboard…</p>;
  }

  return (
    <section aria-label="Guardian dashboard">
      <h1 style={{ fontSize: "2rem" }}>Guardian dashboard</h1>

      {error && (
        <p role="alert" style={{ color: "var(--color-grape)" }}>
          {error}
        </p>
      )}

      <h2 style={{ fontSize: "1.5rem" }}>Reading levels</h2>
      {levelError && (
        <p role="alert" style={{ color: "var(--color-grape)" }}>
          {levelError}
        </p>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "1.1rem" }}>
        <caption className="sr-only">Each child's reading level and recent accuracy</caption>
        <thead>
          <tr style={{ textAlign: "left" }}>
            <th scope="col" style={{ padding: "0.5rem" }}>Child</th>
            <th scope="col" style={{ padding: "0.5rem" }}>Level</th>
            <th scope="col" style={{ padding: "0.5rem" }}>Recent accuracy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ child, mastery }) => {
            const currentLevel = mastery?.level ?? child.readingLevel;
            const levelSelectId = `level-${child.id}`;
            return (
              <tr key={child.id} style={{ borderTop: "2px solid var(--color-sun)" }}>
                <th scope="row" style={{ padding: "0.5rem", textAlign: "left" }}>
                  {child.displayName}
                </th>
                <td style={{ padding: "0.5rem" }}>
                  <label htmlFor={levelSelectId} className="sr-only">
                    Reading level for {child.displayName}
                  </label>
                  <select
                    id={levelSelectId}
                    value={currentLevel}
                    disabled={savingLevelId === child.id}
                    onChange={(e) => void handleChangeLevel(child.id, e.target.value)}
                    style={{
                      fontSize: "1.1rem",
                      padding: "0.35rem 0.5rem",
                      borderRadius: "0.5rem",
                      border: "3px solid var(--color-grape)",
                    }}
                  >
                    {READING_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  {savingLevelId === child.id && (
                    <span role="status" style={{ marginLeft: "0.5rem", fontSize: "0.95rem" }}>
                      Saving…
                    </span>
                  )}
                </td>
                <td style={{ padding: "0.5rem" }}>
                  {mastery ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <div
                        role="img"
                        aria-label={`${child.displayName} passed ${Math.round(mastery.recentAccuracy * 100)}% of recent quizzes`}
                        style={{
                          flex: 1,
                          minWidth: "6rem",
                          height: "0.85rem",
                          background: "var(--color-sun)",
                          borderRadius: "var(--radius-kid)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.round(mastery.recentAccuracy * 100)}%`,
                            height: "100%",
                            background: "var(--color-grape)",
                          }}
                        />
                      </div>
                      <span>{`${Math.round(mastery.recentAccuracy * 100)}%`}</span>
                    </div>
                  ) : (
                    "—"
                  )}
                  {mastery && (
                    <ScoreHistoryGraph
                      scores={mastery.scoreHistory}
                      label={child.displayName}
                    />
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: "0.75rem" }}>
                No children yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p style={{ fontSize: "0.95rem", color: "var(--color-ink)", marginTop: "0.5rem" }}>
        Grown-ups can set a level here to preview harder books and questions.
      </p>

      <h2 style={{ fontSize: "1.5rem", marginTop: "2rem" }}>Add a child</h2>
      <form onSubmit={handleAddChild} aria-label="Add a child">
        <label htmlFor="new-nickname" style={{ display: "block", fontSize: "1.1rem" }}>
          Nickname
        </label>
        <input
          id="new-nickname"
          value={newNickname}
          onChange={(e) => setNewNickname(e.target.value)}
          maxLength={80}
          placeholder="e.g. Sam"
          style={{
            fontSize: "1.25rem",
            padding: "0.5rem 0.75rem",
            borderRadius: "0.75rem",
            border: "3px solid var(--color-grape)",
            marginBottom: "0.75rem",
            display: "block",
          }}
        />
        <fieldset style={{ border: "none", padding: 0, margin: "0 0 0.75rem" }}>
          <legend style={{ fontSize: "1.1rem" }}>Avatar</legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {AVATAR_CHOICES.map((emoji) => (
              <button
                key={emoji}
                type="button"
                aria-pressed={newAvatar === emoji}
                aria-label={`Avatar ${emoji}`}
                onClick={() => setNewAvatar(emoji)}
                style={{
                  fontSize: "1.75rem",
                  width: "3.25rem",
                  height: "3.25rem",
                  borderRadius: "0.75rem",
                  cursor: "pointer",
                  background: newAvatar === emoji ? "var(--color-sun)" : "transparent",
                  border:
                    newAvatar === emoji
                      ? "3px solid var(--color-grape)"
                      : "3px solid var(--color-sun)",
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </fieldset>
        {addError && (
          <p role="alert" style={{ color: "var(--color-grape)" }}>
            {addError}
          </p>
        )}
        <button
          type="submit"
          className="kid-button kid-button--primary"
          disabled={newNickname.trim().length === 0 || adding}
        >
          {adding ? "Adding…" : "Add child"}
        </button>
      </form>

      <h2 style={{ fontSize: "1.5rem", marginTop: "2rem" }}>Tidy up books</h2>
      <div
        className="kid-card"
        style={{ padding: "1.25rem", marginTop: "0.5rem", border: "3px solid var(--color-sun)" }}
      >
        <h3 style={{ fontSize: "1.25rem" }}>Clean up unfinished books</h3>
        <p>
          Sometimes a book stops before all its pages, questions, and answers
          are ready. This removes only those unfinished books — finished books
          stay safe.
        </p>
        {cleanupError && (
          <p role="alert" style={{ color: "var(--color-grape)" }}>
            {cleanupError}
          </p>
        )}
        {cleanupCount !== null && !cleanupError && (
          <p role="status">
            {cleanupCount === 0
              ? "No unfinished books to clean up."
              : `Removed ${cleanupCount} unfinished book${cleanupCount === 1 ? "" : "s"}.`}
          </p>
        )}
        <button
          type="button"
          className="kid-button kid-button--secondary"
          disabled={cleaning}
          onClick={handleCleanupIncomplete}
        >
          {cleaning ? "Cleaning up…" : "Clean up unfinished books"}
        </button>
      </div>

      <div
        className="kid-card"
        style={{ padding: "1.25rem", marginTop: "0.75rem", border: "3px solid var(--color-sun)" }}
      >
        <h3 style={{ fontSize: "1.25rem" }}>Fix missing pictures</h3>
        <p>
          Some pages are meant to have a picture but it did not finish drawing,
          so the book shows a “picture coming soon” spot. This redraws only those
          missing pictures across all finished books. Pages that are meant to be
          words only are left as they are.
        </p>
        {fixError && (
          <p role="alert" style={{ color: "var(--color-grape)" }}>
            {fixError}
          </p>
        )}
        {fixing && fixProgress && (
          <p role="status">
            {fixProgress.total === 0
              ? "Looking for missing pictures…"
              : `Drawing picture ${fixProgress.index} of ${fixProgress.total}` +
                ` — “${fixProgress.book || "your book"}” page ${fixProgress.page}` +
                (fixProgress.failed > 0
                  ? ` (${fixProgress.failed} couldn’t be drawn so far)`
                  : "")}
          </p>
        )}
        {fixing && !fixProgress && <p role="status">Looking for missing pictures…</p>}
        {fixResult && !fixError && (
          <p role="status">
            {fixResult.pagesFixed === 0 && fixResult.pagesFailed === 0
              ? "All pictures are already in place."
              : `Fixed ${fixResult.pagesFixed} picture${
                  fixResult.pagesFixed === 1 ? "" : "s"
                } across ${fixResult.booksScanned} book${
                  fixResult.booksScanned === 1 ? "" : "s"
                }${
                  fixResult.pagesFailed > 0
                    ? `. ${fixResult.pagesFailed} could not be drawn this time — try again in a moment.`
                    : "."
                }`}
          </p>
        )}
        <button
          type="button"
          className="kid-button kid-button--secondary"
          disabled={fixing}
          onClick={handleFixMissingImages}
        >
          {fixing ? "Drawing missing pictures…" : "Fix missing pictures"}
        </button>
      </div>

      <h2 style={{ fontSize: "1.5rem", marginTop: "2rem" }}>Send feedback</h2>
      <div
        className="kid-card"
        style={{ padding: "1.25rem", marginTop: "0.5rem", border: "3px solid var(--color-sun)" }}
      >
        <h3 style={{ fontSize: "1.25rem" }}>Tell us about a bug or an idea</h3>
        <p>
          Found something broken or have a feature idea? Send us a short note.
          Text only — please don’t include any personal details about your
          family.
        </p>
        <form onSubmit={handleSendFeedback} aria-label="Send feedback">
          <fieldset style={{ border: "none", padding: 0, margin: "0 0 0.75rem" }}>
            <legend style={{ fontSize: "1.1rem" }}>Kind</legend>
            <label style={{ marginRight: "1rem", fontSize: "1.1rem" }}>
              <input
                type="radio"
                name="feedback-kind"
                value="bug"
                checked={feedbackKind === "bug"}
                onChange={() => setFeedbackKind("bug")}
              />{" "}
              Bug
            </label>
            <label style={{ fontSize: "1.1rem" }}>
              <input
                type="radio"
                name="feedback-kind"
                value="feature"
                checked={feedbackKind === "feature"}
                onChange={() => setFeedbackKind("feature")}
              />{" "}
              Feature idea
            </label>
          </fieldset>
          <label htmlFor="feedback-title" style={{ display: "block", fontSize: "1.1rem" }}>
            Title
          </label>
          <input
            id="feedback-title"
            value={feedbackTitle}
            onChange={(e) => setFeedbackTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. The book stopped loading"
            style={{
              fontSize: "1.25rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.75rem",
              border: "3px solid var(--color-grape)",
              marginBottom: "0.75rem",
              display: "block",
              width: "min(100%, 32rem)",
            }}
          />
          <label htmlFor="feedback-body" style={{ display: "block", fontSize: "1.1rem" }}>
            Details
          </label>
          <textarea
            id="feedback-body"
            value={feedbackBody}
            onChange={(e) => setFeedbackBody(e.target.value)}
            maxLength={5000}
            rows={4}
            placeholder="What happened, or what would you like to see?"
            style={{
              fontSize: "1.1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.75rem",
              border: "3px solid var(--color-grape)",
              marginBottom: "0.75rem",
              display: "block",
              width: "min(100%, 32rem)",
            }}
          />
          {feedbackError && (
            <p role="alert" style={{ color: "var(--color-grape)" }}>
              {feedbackError}
            </p>
          )}
          {feedbackResult && !feedbackError && (
            <p role="status">
              {feedbackResult.status === "created" && feedbackResult.issueUrl ? (
                <>
                  Thanks! We filed your feedback:{" "}
                  <a href={feedbackResult.issueUrl} target="_blank" rel="noreferrer">
                    view it on GitHub
                  </a>
                  .
                </>
              ) : (
                "Thanks! Your feedback was received."
              )}
            </p>
          )}
          <button
            type="submit"
            className="kid-button kid-button--primary"
            disabled={
              sending ||
              feedbackTitle.trim().length === 0 ||
              feedbackBody.trim().length === 0
            }
          >
            {sending ? "Sending…" : "Send feedback"}
          </button>
        </form>
      </div>

      <h2 style={{ fontSize: "1.5rem", marginTop: "2rem" }}>Your data (Loi 25)</h2>

      <p>Download everything we store about your family.</p>
      <a
        className="kid-button kid-button--secondary"
        href={guardian.exportAllUrl()}
        download
      >
        ⬇ Export all data
      </a>

      <div
        className="kid-card"
        style={{ padding: "1.25rem", marginTop: "1.5rem", border: "3px solid var(--color-blossom)" }}
      >
        <h3 style={{ fontSize: "1.25rem" }}>Delete all data</h3>
        {deleted ? (
          <p role="status">All data has been deleted.</p>
        ) : (
          <>
            <p>
              This permanently removes every child profile, book, and answer.
              This cannot be undone. Type <strong>DELETE</strong> to confirm.
            </p>
            <label htmlFor="confirm-delete" className="sr-only">
              Type DELETE to confirm
            </label>
            <input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              style={{
                fontSize: "1.25rem",
                padding: "0.5rem 0.75rem",
                borderRadius: "0.75rem",
                border: "3px solid var(--color-grape)",
                marginRight: "0.75rem",
              }}
            />
            <button
              type="button"
              className="kid-button"
              style={{ backgroundColor: "var(--color-blossom)", color: "var(--color-ink)" }}
              disabled={confirmText !== "DELETE" || deleting}
              onClick={handleDeleteAll}
            >
              {deleting ? "Deleting…" : "Delete everything"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
